import { ClientStorage } from 'app/shared/lib/client-storage';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { Toaster } from 'app/shared/lib/toaster';
import { Features, FeatureValue, IFeatureSet } from 'app/shared/models/feature-set';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { PassphraseService } from 'app/shared/services/passphrase-service';
import { camelCase, inject } from 'aurelia-framework';

// INFO [Matt] This is the passphrase used to turn feature flags on.
const FEATURE_FLAG_PASSPHRASE = 'enable feature flags';
const FEATURE_FLAG_DISABLE_PASSPHRASE = 'disable feature flags';
const FEATURE_FLAG_PASSPHRASE_KEY = 'enable feature flag';
const FEATURE_FLAG_DISABLE_PASSPHRASE_KEY = 'disable feature flag';
const STORAGE_KEY = 'features';
const FEATURE_KEY_MAP = new WeakMap<IFeatureSet, FeatureKey[]>();

type FeatureKey = string;
type Feature = FeatureKey | FeatureValue;
type Listing = { name: string, description: string, key: string };

@inject(Features, AppLogger, EnvironmentService, PassphraseService, Toaster)
export class FeatureService {

    public enabledFeatures: Record<FeatureKey, boolean>;
    public availableFeatures: Listing[];

    protected storage: ClientStorage;

    constructor(protected Features: IFeatureSet, protected logger: AppLogger, { storage }: EnvironmentService, passphraseService: PassphraseService, toaster: Toaster) {

        this.storage = storage;

        const enabledFeatures: Record<FeatureKey, boolean> = {};
        const availableFeatures: Listing[] = [];

        for(const feature of this.getAllFeatures()) {

            const flag = Features[feature];

            // initialize the feature false unless it is standard
            enabledFeatures[feature] = flag === Features.StandardFeature;

            // if the feature isn't standard, make it available for toggling
            if(flag !== Features.StandardFeature) {
                const key = `ui:features.${camelCase(feature)}`;
                availableFeatures.push({
                    name: `${key}.$`,
                    description: `${key}.description`,
                    key: feature
                });

                // turn on the passphrase to enable non-standard feature

                let password = `${FEATURE_FLAG_PASSPHRASE_KEY} ${feature}`.toLowerCase();
                passphraseService.registerPassphrase(`${FEATURE_FLAG_PASSPHRASE_KEY} ${feature}`.toLowerCase(), () => {
                    // passphraseService.registerPassphrase(password, () => {

                    // include base feature flag functionality too
                    if(enabledFeatures.FeatureFlags === false) {
                        this.toggleFeature(Features.FeatureFlags, true);
                        toaster.standard.activate('ui:features.featureFlags.$', true, true, 1);
                    }

                    if(enabledFeatures[feature] === false) {
                        this.toggleFeature(feature, true);
                        toaster.standard.activate(`${key}.$`, true, true, Infinity);
                    }
                });

                // turn on the passphrase to disable non-standard feature
                passphraseService.registerPassphrase(`${FEATURE_FLAG_DISABLE_PASSPHRASE_KEY} ${feature}`.toLowerCase(), () => {
                    if(enabledFeatures[feature] === true) {
                        this.toggleFeature(feature, false);
                        toaster.standard.activate(`${key}.$`, false, true, Infinity);
                    }
                });
            }
        }
        this.enabledFeatures = enabledFeatures;
        this.availableFeatures = availableFeatures;

        // turn on the pass phrase to enable the feature flags
        passphraseService.registerPassphrase(FEATURE_FLAG_PASSPHRASE, () => {
            if(this.enabledFeatures.FeatureFlags === false) {
                this.toggleFeature(Features.FeatureFlags, true);
                toaster.standard.activate('ui:features.featureFlags.$', true, true, Infinity);
            }
        });

        // turn off the pass phrase to enable the feature flags
        passphraseService.registerPassphrase(FEATURE_FLAG_DISABLE_PASSPHRASE, () => {
            if(this.enabledFeatures.FeatureFlags === true) {
                this.toggleFeature(Features.FeatureFlags, false);
                toaster.standard.activate('ui:features.featureFlags.$', false, true, Infinity);
            }
        });

        this.load();
    }

    public toggleFeature(feature: Feature, value?: boolean) {
        const key = this.getFeatureKey(feature);
        if(key === null) {
            return this.logger.warn('Tried to toggle an unknown feature.')
        }
        if(value === undefined) {
            value = !this.enabledFeatures[key];
        }
        this.updateFeatures({ [key]: value });
    }

    public isFeatureEnabled(feature: Feature) {
        const key = this.getFeatureKey(feature);
        return key !== null && this.enabledFeatures[key] === true;
    }

    protected getFeatureKey(feature: Feature): FeatureKey | null {
        return typeof feature === 'string'
            ? feature
            : this.getAllFeatures().find((key) => this.Features[key] === feature) || null;
    }

    protected getAllFeatures(): FeatureKey[] {
        let features = FEATURE_KEY_MAP.get(this.Features);
        if(!features) {
            features = Reflect.ownKeys(this.Features) as FeatureKey[];
            FEATURE_KEY_MAP.set(this.Features, features);
        }
        return features;
    }

    protected getFeatureName(feature: FeatureValue): FeatureKey | null {
        return this.getAllFeatures()
            .find((key) => this.Features[key as string] === feature) as string || null;
    }

    protected applyFeatures(features: Record<FeatureKey, boolean>) {
        // for each feature applied
        (Reflect.ownKeys(features) as FeatureKey[])
            // exclude standard and unlisted features
            .filter((feature) => {
                const val = this.Features[feature];
                return val && val !== this.Features.StandardFeature
            })
            // and set each feature
            .forEach((feature) => {
                this.enabledFeatures[feature] = features[feature] === true;
            });
    }

    protected updateFeatures(features: Record<FeatureKey, boolean>) {
        this.applyFeatures(features);
        this.save()
    }

    protected save() {
        this.storage.setObject(STORAGE_KEY, this.enabledFeatures);
    }

    protected load() {
        const features = this.storage.getObject(STORAGE_KEY) || {};
        this.applyFeatures(features);
    }
}
